import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, tap, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { AuthenticationService } from 'app/server/services';
import { ErrorService } from '../services/error.service';
import { DialogService } from './dialog-service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
	/**
	 * @param {Router} _router
	 * @param {AuthenticationService} _authenticationService
	 */
	constructor(
		private _router: Router,
		private _authenticationService: AuthenticationService,
		private _errorService: ErrorService,
		private _dialogService: DialogService,
		private _translateService: TranslateService
	) {}

	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		this._errorService._httpLoading.next(true);

		return next.handle(request).pipe(
			catchError(err => {
				if (err.status === 401 && !request.url.includes('/api/notifications')) {
					// Auto redirect for invalid session (skip background notification requests)
					this._router.navigate(['/miscellaneous/not-authorized']);
				}

				if (err.status === 403) {
					this._errorService.markPermissionDenied();

					// Show after local error handlers so this message is not overwritten by generic toasts.
					setTimeout(() => {
						this._dialogService.showToast('warning', this._translateService.instant('common_toast_permission_denied_title'), this._translateService.instant('common_toast_no_permission_operation_msg'));
					});
				}

				// The backend sends a vocabulary key (not a hardcoded Italian sentence) in `errors` — resolve
				// it here, once, so every one of the ~300 call sites that read `err.error.errors`/
				// `err.errorMessage` downstream already gets the translated text without having to know
				// about this convention themselves. Safe no-op for a plain human-readable string that isn't
				// a registered key (translateOrKeepOriginal falls back to it unchanged) — this is also why
				// it's safe to run over `message` too: generic English defaults like "Bad request" never
				// match a vocabulary key (no spaces in real keys), so they pass through unchanged, while
				// the handful of calls that pass a real title key (e.g. "Turno bloccato") get resolved.
				if (typeof err?.error?.errors === 'string') {
					err.error.errors = this.translateOrKeepOriginal(err.error.errors);
				}
				if (typeof err?.error?.message === 'string') {
					err.error.message = this.translateOrKeepOriginal(err.error.message);
				}

				if (err?.error?.code === 422) {
					if (typeof err.error.errors === 'object') {
						let errorMessage: string = '';

						Object.keys(err.error.errors).forEach(key => {
							errorMessage = errorMessage + err.error.errors[key].join(' - ');
							errorMessage += ' ';
						});

						err.errorMessage = errorMessage;
					} else {
						err.errorMessage = err.error.errors;
					}
				}

				this._errorService._httpLoading.next(false);

				// throwError
				const error = err?.error?.message || err.statusText;
				return throwError(err);
			}),
			tap(() => this._errorService._httpLoading.next(false))
		);
	}

	/**
	 * Resolves a backend `errors` string as a vocabulary key. Supports a `<key>::<dynamic value>`
	 * convention for the handful of messages that carry a runtime detail (e.g. a CFU type name, a
	 * command name) — `clientErrorResponse()`/`serverErrorResponse()` only accept a plain string, so
	 * this is the simplest way to pass one param through without changing that shared trait's signature.
	 * Falls back to the original string unchanged if it isn't a registered key (e.g. a message not yet
	 * migrated off hardcoded Italian text, or a raw exception message in local/dev).
	 */
	private translateOrKeepOriginal(raw: string): string {
		if (!raw) {
			return raw;
		}

		const direct = this._translateService.instant(raw);
		if (direct !== raw) {
			return direct;
		}

		const sepIndex = raw.indexOf('::');
		if (sepIndex !== -1) {
			const key = raw.substring(0, sepIndex);
			const value = raw.substring(sepIndex + 2);
			const translated = this._translateService.instant(key, { value });
			if (translated !== key) {
				return translated;
			}
		}

		return raw;
	}
}
